import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { 
  collection, getDocs, query, where, orderBy, Timestamp, 
  doc, updateDoc, runTransaction, getDoc 
} from 'firebase/firestore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export const SalesLedger = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('month');
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('all');
  const [viewMode, setViewMode] = useState('product');
  const [selectedProductForDetails, setSelectedProductForDetails] = useState(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closedPeriods, setClosedPeriods] = useState([]);
  const [closingInProgress, setClosingInProgress] = useState(false);
  const [showCOGSModal, setShowCOGSModal] = useState(false);
  const [selectedSaleForCOGS, setSelectedSaleForCOGS] = useState(null);
  const [updatingCOGS, setUpdatingCOGS] = useState(false);
  
  // New state for BOM items
  const [bomItems, setBomItems] = useState([
    { id: Date.now(), materialName: '', quantity: '', unitCost: '' }
  ]);

  useEffect(() => {
    fetchProducts();
    fetchSales();
    fetchClosedPeriods();
  }, [dateRange]);

  const fetchClosedPeriods = async () => {
    try {
      const closedRef = collection(db, 'closedPeriods');
      const snapshot = await getDocs(query(closedRef, orderBy('periodDate', 'desc')));
      const periods = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClosedPeriods(periods);
    } catch (error) {
      console.error('Error fetching closed periods:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'products'));
      const productsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProducts(productsList);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchSales = async () => {
    setLoading(true);
    try {
      let startDate = null;
      const now = new Date();
      
      switch (dateRange) {
        case 'week':
          startDate = new Date(now.setDate(now.getDate() - 7));
          break;
        case 'month':
          startDate = new Date(now.setMonth(now.getMonth() - 1));
          break;
        case 'year':
          startDate = new Date(now.setFullYear(now.getFullYear() - 1));
          break;
        default:
          startDate = null;
      }

      let ordersQuery = collection(db, 'orders');
      if (startDate) {
        ordersQuery = query(
          ordersQuery,
          where('orderDate', '>=', Timestamp.fromDate(startDate)),
          orderBy('orderDate', 'desc')
        );
      } else {
        ordersQuery = query(ordersQuery, orderBy('orderDate', 'desc'));
      }

      const ordersSnapshot = await getDocs(ordersQuery);
      const salesData = [];
      
      for (const orderDoc of ordersSnapshot.docs) {
        const order = { id: orderDoc.id, ...orderDoc.data() };
        
        const itemsRef = collection(db, 'orderItems');
        const itemsQuery = query(itemsRef, where('orderId', '==', order.id));
        const itemsSnapshot = await getDocs(itemsQuery);
        const items = itemsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        let clientName = 'Unknown';
        let clientPhone = '';
        let clientEmail = '';
        if (order.clientId) {
          const clientDoc = await getDocs(query(collection(db, 'clients'), where('__name__', '==', order.clientId)));
          if (!clientDoc.empty) {
            const clientData = clientDoc.docs[0].data();
            clientName = clientData.name || 'Unknown';
            clientPhone = clientData.phone || '';
            clientEmail = clientData.email || '';
          }
        }
        
        salesData.push({ 
          ...order, 
          items, 
          clientName,
          clientPhone,
          clientEmail
        });
      }
      
      setSales(salesData);
    } catch (error) {
      console.error('Error fetching sales:', error);
    } finally {
      setLoading(false);
    }
  };

  // Updated: now accepts an array of BOM items, calculates total, and stores BOM details
  const updateItemCOGSWithBOM = async (orderId, itemId, bomItemsArray) => {
    setUpdatingCOGS(true);
    try {
      // Calculate total additional cost from all BOM rows
      const totalAdditional = bomItemsArray.reduce((sum, item) => {
        const qty = parseFloat(item.quantity) || 0;
        const cost = parseFloat(item.unitCost) || 0;
        return sum + (qty * cost);
      }, 0);

      if (totalAdditional <= 0) {
        alert('Please add at least one valid material with positive cost.');
        return;
      }

      const itemRef = doc(db, 'orderItems', itemId);
      const itemDoc = await getDoc(itemRef);
      const currentItem = itemDoc.data();
      
      if (!currentItem) {
        alert('Item not found');
        return;
      }
      
      const newTotalCOGS = (currentItem.totalCOGS || 0) + totalAdditional;
      const newUnitCOGS = newTotalCOGS / currentItem.quantity;
      
      // Prepare BOM entry with full details
      const bomEntry = {
        amount: totalAdditional,
        description: 'Bill of Materials',
        date: Timestamp.now(),
        addedBy: 'system',
        bomItems: bomItemsArray.map(bom => ({
          materialName: bom.materialName,
          quantity: parseFloat(bom.quantity),
          unitCost: parseFloat(bom.unitCost),
          lineTotal: (parseFloat(bom.quantity) || 0) * (parseFloat(bom.unitCost) || 0)
        }))
      };
      
      await updateDoc(itemRef, {
        totalCOGS: newTotalCOGS,
        unitCOGS: newUnitCOGS,
        additionalCosts: [...(currentItem.additionalCosts || []), bomEntry],
        updatedAt: Timestamp.now()
      });
      
      // Update order total COGS
      const orderRef = doc(db, 'orders', orderId);
      const orderDoc = await getDoc(orderRef);
      const currentOrder = orderDoc.data();
      
      if (currentOrder) {
        const newOrderCOGS = (currentOrder.totalCOGS || 0) + totalAdditional;
        await updateDoc(orderRef, {
          totalCOGS: newOrderCOGS,
          updatedAt: Timestamp.now()
        });
      }
      
      await fetchSales();
      alert(`Added BOM total of ${totalAdditional.toLocaleString()} UGX to COGS.`);
      setShowCOGSModal(false);
      // Reset BOM items for next use
      setBomItems([{ id: Date.now(), materialName: '', quantity: '', unitCost: '' }]);
      setSelectedSaleForCOGS(null);
    } catch (error) {
      console.error('Error updating COGS with BOM:', error);
      alert('Error updating COGS: ' + error.message);
    } finally {
      setUpdatingCOGS(false);
    }
  };

  // BOM row handlers
  const addBOMItem = () => {
    setBomItems([...bomItems, { id: Date.now(), materialName: '', quantity: '', unitCost: '' }]);
  };

  const removeBOMItem = (id) => {
    if (bomItems.length === 1) return;
    setBomItems(bomItems.filter(item => item.id !== id));
  };

  const updateBOMItem = (id, field, value) => {
    setBomItems(bomItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const getBOMTotal = () => {
    return bomItems.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const cost = parseFloat(item.unitCost) || 0;
      return sum + (qty * cost);
    }, 0);
  };

  const getAvailableMonths = () => {
    const months = new Map();
    const closedPeriodSet = new Set(closedPeriods.map(p => p.period));
    
    sales.forEach(sale => {
      const date = sale.orderDate?.toDate();
      if (date) {
        const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (closedPeriodSet.has(yearMonth)) return;
        
        const monthName = date.toLocaleString('default', { month: 'long', year: 'numeric' });
        if (!months.has(yearMonth)) {
          months.set(yearMonth, { 
            period: yearMonth, 
            name: monthName,
            totalRevenue: 0,
            totalCOGS: 0,
            totalProfit: 0,
            orderCount: 0
          });
        }
        const monthData = months.get(yearMonth);
        monthData.totalRevenue += sale.totalSellingPrice || 0;
        monthData.totalCOGS += sale.totalCOGS || 0;
        monthData.totalProfit += (sale.totalSellingPrice || 0) - (sale.totalCOGS || 0);
        monthData.orderCount += 1;
      }
    });
    return Array.from(months.values()).sort((a, b) => b.period.localeCompare(a.period));
  };

  const closeMonth = async (period) => {
    if (!window.confirm(`Are you sure you want to close ${period.name}? This will prevent any modifications to sales in this period.`)) {
      return;
    }

    setClosingInProgress(true);
    try {
      await runTransaction(db, async (transaction) => {
        const closedPeriodRef = doc(collection(db, 'closedPeriods'));
        transaction.set(closedPeriodRef, {
          period: period.period,
          periodName: period.name,
          status: 'closed',
          closedDate: Timestamp.now(),
          closedBy: 'system',
          summary: {
            totalRevenue: period.totalRevenue,
            totalCOGS: period.totalCOGS,
            totalProfit: period.totalProfit,
            orderCount: period.orderCount
          },
          createdAt: Timestamp.now()
        });

        const [year, month] = period.period.split('-');
        const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        const endDate = new Date(parseInt(year), parseInt(month), 0);
        
        const ordersQuery = query(
          collection(db, 'orders'),
          where('orderDate', '>=', Timestamp.fromDate(startDate)),
          where('orderDate', '<=', Timestamp.fromDate(endDate))
        );
        const ordersSnapshot = await getDocs(ordersQuery);
        
        for (const orderDoc of ordersSnapshot.docs) {
          transaction.update(doc(db, 'orders', orderDoc.id), {
            isClosed: true,
            closedPeriod: period.period,
            updatedAt: Timestamp.now()
          });
        }
      });

      await fetchClosedPeriods();
      await fetchSales();
      setShowCloseModal(false);
      alert(`Successfully closed ${period.name}`);
    } catch (error) {
      console.error('Error closing month:', error);
      alert('Error closing month: ' + error.message);
    } finally {
      setClosingInProgress(false);
    }
  };

  const generateMonthEndReport = async (period) => {
    const pdfDoc = new jsPDF('landscape');
    const pageWidth = pdfDoc.internal.pageSize.width;
    
    const [year, month] = period.period.split('-');
    const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
    const endDate = new Date(parseInt(year), parseInt(month), 0);
    
    const periodSales = sales.filter(sale => {
      const saleDate = sale.orderDate?.toDate();
      return saleDate && saleDate >= startDate && saleDate <= endDate;
    });

    pdfDoc.setFillColor(127, 26, 46);
    pdfDoc.rect(0, 0, pageWidth, 40, 'F');
    pdfDoc.setTextColor(255, 255, 255);
    pdfDoc.setFontSize(20);
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text('MONTH-END SALES REPORT', pageWidth / 2, 25, { align: 'center' });
    
    pdfDoc.setTextColor(0, 0, 0);
    pdfDoc.setFontSize(12);
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text(period.name, pageWidth / 2, 52, { align: 'center' });
    
    pdfDoc.setFontSize(10);
    pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.text(`Generated: ${new Date().toLocaleString()}`, 20, 62);
    pdfDoc.text(`Period: ${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`, 20, 69);
    pdfDoc.text(`Total Orders: ${periodSales.length}`, 20, 76);
    
    const totalRevenue = periodSales.reduce((sum, s) => sum + (s.totalSellingPrice || 0), 0);
    const totalCOGS = periodSales.reduce((sum, s) => sum + (s.totalCOGS || 0), 0);
    const totalProfit = totalRevenue - totalCOGS;
    const margin = totalRevenue > 0 ? (totalProfit / totalRevenue * 100).toFixed(1) : 0;
    
    pdfDoc.setFillColor(240, 240, 240);
    pdfDoc.rect(20, 86, pageWidth - 40, 40, 'F');
    pdfDoc.setFontSize(11);
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text('FINANCIAL SUMMARY', 25, 98);
    pdfDoc.setFontSize(9);
    pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.text(`Total Revenue: ${totalRevenue.toLocaleString()} UGX`, 25, 108);
    pdfDoc.text(`Total COGS: ${totalCOGS.toLocaleString()} UGX`, 25, 116);
    pdfDoc.text(`Gross Profit: ${totalProfit.toLocaleString()} UGX`, 25, 124);
    pdfDoc.text(`Profit Margin: ${margin}%`, 25, 132);
    
    const productSummary = new Map();
    for (const sale of periodSales) {
      for (const item of sale.items) {
        if (!productSummary.has(item.productId)) {
          productSummary.set(item.productId, {
            productName: item.productName,
            quantity: 0,
            revenue: 0,
            cogs: 0
          });
        }
        const summary = productSummary.get(item.productId);
        summary.quantity += item.quantity;
        summary.revenue += item.totalSellingPrice || 0;
        summary.cogs += item.totalCOGS || 0;
      }
    }
    
    const productTableData = Array.from(productSummary.values()).map(p => [
      p.productName,
      p.quantity.toString(),
      p.revenue.toLocaleString(),
      p.cogs.toLocaleString(),
      (p.revenue - p.cogs).toLocaleString(),
      p.revenue > 0 ? ((p.revenue - p.cogs) / p.revenue * 100).toFixed(1) + '%' : '0%'
    ]);
    
    autoTable(pdfDoc, {
      startY: 150,
      head: [['Product', 'Qty Sold', 'Revenue (UGX)', 'COGS (UGX)', 'Profit (UGX)', 'Margin']],
      body: productTableData,
      theme: 'striped',
      headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255] },
      styles: { fontSize: 8 }
    });
    
    pdfDoc.save(`Month_End_Report_${period.period}.pdf`);
  };

  const getProductSales = () => {
    const productSalesMap = new Map();
    const closedPeriodSet = new Set(closedPeriods.map(p => p.period));
    
    for (const sale of sales) {
      const saleDate = sale.orderDate?.toDate();
      if (saleDate) {
        const yearMonth = `${saleDate.getFullYear()}-${String(saleDate.getMonth() + 1).padStart(2, '0')}`;
        if (closedPeriodSet.has(yearMonth)) continue;
      }
      
      for (const item of sale.items) {
        const productId = item.productId;
        if (!productSalesMap.has(productId)) {
          productSalesMap.set(productId, {
            productId: productId,
            productName: item.productName,
            sales: [],
            totalRevenue: 0,
            totalCOGS: 0,
            totalProfit: 0,
            totalQuantity: 0
          });
        }
        
        const productSale = productSalesMap.get(productId);
        const revenue = item.totalSellingPrice || 0;
        const cogs = item.totalCOGS || 0;
        const profit = revenue - cogs;
        
        productSale.sales.push({
          orderId: sale.id,
          orderItemId: item.id,
          orderNumber: sale.orderNumber,
          orderDate: sale.orderDate,
          clientId: sale.clientId,
          clientName: sale.clientName,
          clientPhone: sale.clientPhone,
          clientEmail: sale.clientEmail,
          quantity: item.quantity,
          unitPrice: item.unitSellingPrice,
          unitCOGS: item.unitCOGS,
          revenue: revenue,
          cogs: cogs,
          profit: profit,
          status: sale.status,
          additionalCosts: item.additionalCosts || []
        });
        
        productSale.totalRevenue += revenue;
        productSale.totalCOGS += cogs;
        productSale.totalProfit += profit;
        productSale.totalQuantity += item.quantity;
      }
    }
    
    return Array.from(productSalesMap.values());
  };

  const calculateOrderTotals = () => {
    let filteredSales = sales;
    if (selectedProductId !== 'all') {
      filteredSales = sales.filter(sale => 
        sale.items.some(item => item.productId === selectedProductId)
      );
    }
    
    const totalRevenue = filteredSales.reduce((sum, sale) => sum + (sale.totalSellingPrice || 0), 0);
    const totalCOGS = filteredSales.reduce((sum, sale) => sum + (sale.totalCOGS || 0), 0);
    const grossProfit = totalRevenue - totalCOGS;
    const margin = totalRevenue > 0 ? (grossProfit / totalRevenue * 100).toFixed(1) : 0;
    
    return { totalRevenue, totalCOGS, grossProfit, margin };
  };

  const getFilteredProductSales = () => {
    const allProductSales = getProductSales();
    if (selectedProductId !== 'all') {
      return allProductSales.filter(ps => ps.productId === selectedProductId);
    }
    return allProductSales;
  };

  const calculateProductTotals = () => {
    const filteredProductSales = getFilteredProductSales();
    const totalRevenue = filteredProductSales.reduce((sum, ps) => sum + ps.totalRevenue, 0);
    const totalCOGS = filteredProductSales.reduce((sum, ps) => sum + ps.totalCOGS, 0);
    const grossProfit = totalRevenue - totalCOGS;
    const margin = totalRevenue > 0 ? (grossProfit / totalRevenue * 100).toFixed(1) : 0;
    
    return { totalRevenue, totalCOGS, grossProfit, margin };
  };

  const orderTotals = calculateOrderTotals();
  const productTotals = calculateProductTotals();
  const productSales = getFilteredProductSales();
  const availableMonths = getAvailableMonths();

  if (loading) {
    return (
      <div style={localStyles.loadingContainer}>
        <div style={localStyles.spinner}></div>
        <p>Loading sales data...</p>
      </div>
    );
  }

  // PRODUCT ACCOUNT VIEW
  if (viewMode === 'product') {
    const selectedProductData = productSales.find(p => p.productId === selectedProductForDetails);
    
    return (
      <div style={localStyles.container}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <div style={localStyles.viewToggle}>
            <button
              style={{...localStyles.toggleButton, ...(viewMode === 'order' ? localStyles.toggleButtonActive : {})}}
              onClick={() => setViewMode('order')}
            >
              Order View
            </button>
            <button
              style={{...localStyles.toggleButton, ...(viewMode === 'product' ? localStyles.toggleButtonActive : {})}}
              onClick={() => setViewMode('product')}
            >
              Product Account View
            </button>
          </div>
          <button 
            style={{
              padding: '10px 20px',
              background: '#7f1a2e',
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
            onClick={() => setShowCloseModal(true)}
          >
            Month-End Closing
          </button>
        </div>

        {closedPeriods.length > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            background: '#f8fafc',
            borderRadius: '12px',
            marginBottom: '20px',
            fontSize: '13px',
            color: '#475569',
            flexWrap: 'wrap'
          }}>
            <span>Closed Periods: </span>
            {closedPeriods.map(period => (
              <span key={period.id} style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                background: '#e2e8f0',
                borderRadius: '20px',
                fontSize: '12px',
                color: '#1e293b'
              }}>
                {period.periodName}
                <button 
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    padding: '2px 4px'
                  }}
                  onClick={() => generateMonthEndReport(period)}
                >
                  View
                </button>
              </span>
            ))}
          </div>
        )}

        <div style={localStyles.kpiGrid}>
          <div style={localStyles.kpiCard}>
            <div style={localStyles.kpiIcon}>💰</div>
            <div>
              <div style={localStyles.kpiLabel}>Total Revenue</div>
              <div style={localStyles.kpiValue}>{productTotals.totalRevenue.toLocaleString()} UGX</div>
            </div>
          </div>
          <div style={localStyles.kpiCard}>
            <div style={localStyles.kpiIcon}>🏭</div>
            <div>
              <div style={localStyles.kpiLabel}>Total COGS</div>
              <div style={localStyles.kpiValue}>{productTotals.totalCOGS.toLocaleString()} UGX</div>
            </div>
          </div>
          <div style={localStyles.kpiCard}>
            <div style={localStyles.kpiIcon}>📈</div>
            <div>
              <div style={localStyles.kpiLabel}>Gross Profit</div>
              <div style={localStyles.kpiValue}>{productTotals.grossProfit.toLocaleString()} UGX</div>
            </div>
          </div>
          <div style={localStyles.kpiCard}>
            <div style={localStyles.kpiIcon}>🎯</div>
            <div>
              <div style={localStyles.kpiLabel}>Profit Margin</div>
              <div style={localStyles.kpiValue}>{productTotals.margin}%</div>
            </div>
          </div>
        </div>

        <div style={localStyles.filtersBar}>
          <div style={localStyles.filterGroup}>
            <label style={localStyles.filterLabel}>Date Range</label>
            <select style={localStyles.filterSelect} value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="year">Last 12 Months</option>
              <option value="all">All Time</option>
            </select>
          </div>
          <div style={localStyles.filterGroup}>
            <label style={localStyles.filterLabel}>Product Account</label>
            <select style={localStyles.filterSelect} value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value)}>
              <option value="all">All Products</option>
              {products.map(product => (
                <option key={product.id} value={product.id}>{product.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={localStyles.tableWrapper}>
          <table style={localStyles.table}>
            <thead>
              <tr style={localStyles.tableHeaderRow}>
                <th style={localStyles.tableHeader}>Product Account</th>
                <th style={localStyles.tableHeader}>Model</th>
                <th style={localStyles.tableHeader}>Units Sold</th>
                <th style={localStyles.tableHeader}>Total Revenue</th>
                <th style={localStyles.tableHeader}>Total COGS</th>
                <th style={localStyles.tableHeader}>Gross Profit</th>
                <th style={localStyles.tableHeader}>Margin</th>
                <th style={localStyles.tableHeader}>Transactions</th>
                <th style={localStyles.tableHeader}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {productSales.map(productSale => {
                const product = products.find(p => p.id === productSale.productId);
                const profit = productSale.totalProfit;
                const marginPercent = productSale.totalRevenue > 0 ? (profit / productSale.totalRevenue * 100).toFixed(1) : 0;
                
                return (
                  <tr key={productSale.productId} style={localStyles.tableRow}>
                    <td style={{...localStyles.tableCell, fontWeight: '600', color: '#7f1a2e'}}>
                      {productSale.productName}
                    </td>
                    <td style={localStyles.tableCell}>{product?.modelNumber || '-'}</td>
                    <td style={localStyles.tableCell}>{productSale.totalQuantity} units</td>
                    <td style={localStyles.tableCell}>{productSale.totalRevenue.toLocaleString()} UGX</td>
                    <td style={localStyles.tableCell}>{productSale.totalCOGS.toLocaleString()} UGX</td>
                    <td style={{...localStyles.tableCell, ...(profit > 0 ? localStyles.profitPositive : localStyles.profitNegative)}}>
                      {profit.toLocaleString()} UGX
                    </td>
                    <td style={{...localStyles.tableCell, ...(marginPercent > 0 ? localStyles.profitPositive : localStyles.profitNegative)}}>
                      {marginPercent}%
                    </td>
                    <td style={localStyles.tableCell}>{productSale.sales.length} sales</td>
                    <td style={localStyles.tableCell}>
                      <button 
                        style={{
                          padding: '6px 12px',
                          background: '#f1f5f9',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '11px',
                          cursor: 'pointer',
                          color: '#475569'
                        }}
                        onClick={() => setSelectedProductForDetails(productSale.productId)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })}
              {productSales.length === 0 && (
                <tr>
                  <td colSpan="9" style={localStyles.emptyState}>No product sales found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {showCloseModal && (
          <div style={modalStyles.overlay} onClick={() => setShowCloseModal(false)}>
            <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
              <div style={modalStyles.header}>
                <h2 style={modalStyles.title}>Month-End Closing</h2>
                <button style={modalStyles.closeButton} onClick={() => setShowCloseModal(false)}>×</button>
              </div>
              
              <div style={modalStyles.form}>
                {closedPeriods.length > 0 && (
                  <div style={{ marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid #eef2f6' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>Previously Closed Periods</h4>
                    {closedPeriods.map(period => (
                      <div key={period.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <span>{period.periodName}</span>
                        <button 
                          style={{ padding: '4px 10px', background: '#f1f5f9', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}
                          onClick={() => generateMonthEndReport(period)}
                        >
                          View Report
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {availableMonths.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>Available Months to Close</h4>
                    {availableMonths.map(month => (
                      <div key={month.period} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#f8fafc', borderRadius: '12px', marginBottom: '8px' }}>
                        <div>
                          <strong>{month.name}</strong>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>{month.orderCount} orders · {month.totalRevenue.toLocaleString()} UGX</div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button 
                            style={{ padding: '6px 12px', background: '#f1f5f9', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                            onClick={() => generateMonthEndReport(month)}
                          >
                            Report
                          </button>
                          <button 
                            style={{ padding: '6px 12px', background: '#7f1a2e', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                            onClick={() => closeMonth(month)}
                            disabled={closingInProgress}
                          >
                            {closingInProgress ? 'Closing...' : 'Close Month'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {availableMonths.length === 0 && closedPeriods.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No sales data available for month-end closing.</div>
                )}

                <div style={{ marginTop: '20px', padding: '12px', background: '#fef3c7', borderRadius: '10px', fontSize: '12px', color: '#d97706' }}>
                  <strong>Important:</strong> Once a month is closed, you cannot modify any sales from that period. This action is irreversible.
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                  <button style={{ flex: 1, background: '#f1f5f9', color: '#334155', padding: '12px', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }} onClick={() => setShowCloseModal(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Product Sales Detail Modal */}
        {selectedProductForDetails && selectedProductData && (
          <div style={detailModalStyles.overlay} onClick={() => setSelectedProductForDetails(null)}>
            <div style={detailModalStyles.container} onClick={(e) => e.stopPropagation()}>
              <div style={detailModalStyles.header}>
                <div>
                  <h2 style={detailModalStyles.title}>Product Sales Details</h2>
                  <p style={detailModalStyles.subtitle}>
                    {selectedProductData.productName} - 
                    {products.find(p => p.id === selectedProductForDetails)?.modelNumber || ''}
                  </p>
                </div>
                <button style={detailModalStyles.closeButton} onClick={() => setSelectedProductForDetails(null)}>×</button>
              </div>

              {/* Summary Section */}
              <div style={detailModalStyles.summarySection}>
                <div style={detailModalStyles.summaryGrid}>
                  <div style={detailModalStyles.summaryItem}>
                    <div style={detailModalStyles.summaryLabel}>Total Units Sold</div>
                    <div style={detailModalStyles.summaryValue}>{selectedProductData.totalQuantity} units</div>
                  </div>
                  <div style={detailModalStyles.summaryItem}>
                    <div style={detailModalStyles.summaryLabel}>Total Revenue</div>
                    <div style={detailModalStyles.summaryValue}>{selectedProductData.totalRevenue.toLocaleString()} UGX</div>
                  </div>
                  <div style={detailModalStyles.summaryItem}>
                    <div style={detailModalStyles.summaryLabel}>Total COGS</div>
                    <div style={detailModalStyles.summaryValue}>{selectedProductData.totalCOGS.toLocaleString()} UGX</div>
                  </div>
                  <div style={detailModalStyles.summaryItem}>
                    <div style={detailModalStyles.summaryLabel}>Gross Profit</div>
                    <div style={{...detailModalStyles.summaryValue, color: selectedProductData.totalProfit > 0 ? '#10b981' : '#ef4444'}}>
                      {selectedProductData.totalProfit.toLocaleString()} UGX
                    </div>
                  </div>
                  <div style={detailModalStyles.summaryItem}>
                    <div style={detailModalStyles.summaryLabel}>Profit Margin</div>
                    <div style={{...detailModalStyles.summaryValue, color: (selectedProductData.totalProfit / selectedProductData.totalRevenue * 100) > 0 ? '#10b981' : '#ef4444'}}>
                      {((selectedProductData.totalProfit / selectedProductData.totalRevenue) * 100).toFixed(1)}%
                    </div>
                  </div>
                  <div style={detailModalStyles.summaryItem}>
                    <div style={detailModalStyles.summaryLabel}>Total Transactions</div>
                    <div style={detailModalStyles.summaryValue}>{selectedProductData.sales.length}</div>
                  </div>
                </div>
              </div>

              {/* Sales Details Table */}
              <div style={detailModalStyles.tableSection}>
                <h4 style={detailModalStyles.sectionTitle}>Sales Transactions</h4>
                <div style={detailModalStyles.tableWrapper}>
                  <table style={detailModalStyles.table}>
                    <thead>
                      <tr style={detailModalStyles.tableHeaderRow}>
                        <th style={detailModalStyles.tableHeader}>Date</th>
                        <th style={detailModalStyles.tableHeader}>Order #</th>
                        <th style={detailModalStyles.tableHeader}>Client Name</th>
                        <th style={detailModalStyles.tableHeader}>Phone</th>
                        <th style={detailModalStyles.tableHeader}>Quantity</th>
                        <th style={detailModalStyles.tableHeader}>Revenue</th>
                        <th style={detailModalStyles.tableHeader}>COGS</th>
                        <th style={detailModalStyles.tableHeader}>Profit</th>
                        <th style={detailModalStyles.tableHeader}>Status</th>
                        <th style={detailModalStyles.tableHeader}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedProductData.sales.map((sale, index) => (
                        <tr key={index} style={detailModalStyles.tableRow}>
                          <td style={detailModalStyles.tableCell}>{new Date(sale.orderDate?.toDate()).toLocaleDateString()}</td>
                          <td style={{...detailModalStyles.tableCell, color: '#7f1a2e'}}>{sale.orderNumber}</td>
                          <td style={detailModalStyles.tableCell}><strong>{sale.clientName}</strong></td>
                          <td style={detailModalStyles.tableCell}>{sale.clientPhone || '-'}</td>
                          <td style={detailModalStyles.tableCell}>{sale.quantity}</td>
                          <td style={detailModalStyles.tableCell}>{sale.revenue.toLocaleString()} UGX</td>
                          <td style={{...detailModalStyles.tableCell, color: '#f59e0b'}}>
                            {sale.cogs.toLocaleString()} UGX
                          </td>
                          <td style={{...detailModalStyles.tableCell, color: sale.profit > 0 ? '#10b981' : '#ef4444'}}>
                            {sale.profit.toLocaleString()} UGX
                          </td>
                          <td style={detailModalStyles.tableCell}>
                            <span style={{...detailModalStyles.statusBadge, ...detailModalStyles[`status${sale.status}`]}}>
                              {sale.status}
                            </span>
                          </td>
                          <td style={detailModalStyles.tableCell}>
                            <button
                              style={detailModalStyles.addCostButton}
                              onClick={() => {
                                setSelectedSaleForCOGS(sale);
                                // Reset BOM items when opening modal
                                setBomItems([{ id: Date.now(), materialName: '', quantity: '', unitCost: '' }]);
                                setShowCOGSModal(true);
                              }}
                            >
                              Add BOM
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={detailModalStyles.tableFooterRow}>
                        <td colSpan="5" style={detailModalStyles.tableFooterLabel}>TOTALS:</td>
                        <td style={detailModalStyles.tableFooterValue}>
                          {selectedProductData.totalRevenue.toLocaleString()} UGX
                        </td>
                        <td style={detailModalStyles.tableFooterValue}>
                          {selectedProductData.totalCOGS.toLocaleString()} UGX
                        </td>
                        <td style={detailModalStyles.tableFooterValue}>
                          {selectedProductData.totalProfit.toLocaleString()} UGX
                        </td>
                        <td colSpan="2"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div style={detailModalStyles.footer}>
                <button style={detailModalStyles.closeButtonFooter} onClick={() => setSelectedProductForDetails(null)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* New BOM Modal (replaces old COGS modal) */}
        {showCOGSModal && selectedSaleForCOGS && (
          <div style={cogsModalStyles.overlay} onClick={() => setShowCOGSModal(false)}>
            <div style={cogsModalStyles.container} onClick={(e) => e.stopPropagation()}>
              <div style={cogsModalStyles.header}>
                <h2 style={cogsModalStyles.title}>Bill of Materials (Add Cost)</h2>
                <button style={cogsModalStyles.closeButton} onClick={() => setShowCOGSModal(false)}>×</button>
              </div>
              <div style={cogsModalStyles.form}>
                <div style={cogsModalStyles.formGroup}>
                  <label style={cogsModalStyles.label}>Order Number</label>
                  <input type="text" style={cogsModalStyles.input} value={selectedSaleForCOGS.orderNumber} disabled />
                </div>
                <div style={cogsModalStyles.formGroup}>
                  <label style={cogsModalStyles.label}>Product</label>
                  <input type="text" style={cogsModalStyles.input} value={selectedProductData?.productName || ''} disabled />
                </div>
                <div style={cogsModalStyles.formGroup}>
                  <label style={cogsModalStyles.label}>Current COGS</label>
                  <input type="text" style={cogsModalStyles.input} value={`${selectedSaleForCOGS.cogs.toLocaleString()} UGX`} disabled />
                </div>

                {/* BOM Items Table */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={cogsModalStyles.label}>Materials / Costs</label>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead style={{ background: '#f8fafc' }}>
                        <tr>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Material Name</th>
                          <th style={{ padding: '8px', textAlign: 'center', width: '80px' }}>Qty</th>
                          <th style={{ padding: '8px', textAlign: 'center', width: '100px' }}>Unit Cost (UGX)</th>
                          <th style={{ padding: '8px', textAlign: 'right', width: '100px' }}>Total</th>
                          <th style={{ width: '40px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {bomItems.map((item) => {
                          const lineTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unitCost) || 0);
                          return (
                            <tr key={item.id} style={{ borderTop: '1px solid #eef2f6' }}>
                              <td style={{ padding: '8px' }}>
                                <input
                                  type="text"
                                  style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                                  placeholder="e.g., Plywood"
                                  value={item.materialName}
                                  onChange={(e) => updateBOMItem(item.id, 'materialName', e.target.value)}
                                />
                              </td>
                              <td style={{ padding: '8px' }}>
                                <input
                                  type="number"
                                  style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '6px', textAlign: 'center' }}
                                  placeholder="0"
                                  value={item.quantity}
                                  onChange={(e) => updateBOMItem(item.id, 'quantity', e.target.value)}
                                />
                              </td>
                              <td style={{ padding: '8px' }}>
                                <input
                                  type="number"
                                  style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '6px', textAlign: 'center' }}
                                  placeholder="0"
                                  value={item.unitCost}
                                  onChange={(e) => updateBOMItem(item.id, 'unitCost', e.target.value)}
                                />
                              </td>
                              <td style={{ padding: '8px', textAlign: 'right', fontWeight: '500' }}>
                                {lineTotal.toLocaleString()} UGX
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>
                                <button
                                  type="button"
                                  onClick={() => removeBOMItem(item.id)}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '16px' }}
                                  disabled={bomItems.length === 1}
                                >
                                  ✖
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="button"
                    onClick={addBOMItem}
                    style={{ marginTop: '12px', padding: '6px 12px', background: '#f1f5f9', border: 'none', borderRadius: '8px', fontSize: '12px', cursor: 'pointer' }}
                  >
                    + Add Material
                  </button>
                </div>

                {/* Total */}
                <div style={{ textAlign: 'right', padding: '12px', background: '#f8fafc', borderRadius: '12px', marginBottom: '16px' }}>
                  <strong style={{ fontSize: '14px' }}>Total Additional Cost: {getBOMTotal().toLocaleString()} UGX</strong>
                </div>

                <div style={cogsModalStyles.warningNote}>
                  This total will be added to the COGS for this specific order item. The profit margin will be recalculated automatically.
                </div>

                <div style={cogsModalStyles.actions}>
                  <button style={cogsModalStyles.buttonSecondary} onClick={() => setShowCOGSModal(false)}>Cancel</button>
                  <button
                    style={cogsModalStyles.buttonPrimary}
                    onClick={() => updateItemCOGSWithBOM(selectedSaleForCOGS.orderId, selectedSaleForCOGS.orderItemId, bomItems)}
                    disabled={updatingCOGS || getBOMTotal() === 0}
                  >
                    {updatingCOGS ? 'Updating...' : 'Add BOM & Update COGS'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ORDER VIEW
  return (
    <div style={localStyles.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div style={localStyles.viewToggle}>
          <button
            style={{...localStyles.toggleButton, ...(viewMode === 'order' ? localStyles.toggleButtonActive : {})}}
            onClick={() => setViewMode('order')}
          >
            Order View
          </button>
          <button
            style={{...localStyles.toggleButton, ...(viewMode === 'product' ? localStyles.toggleButtonActive : {})}}
            onClick={() => setViewMode('product')}
          >
            Product Account View
          </button>
        </div>
        <button 
          style={{
            padding: '10px 20px',
            background: '#7f1a2e',
            color: '#ffffff',
            border: 'none',
            borderRadius: '10px',
            fontSize: '13px',
            fontWeight: '500',
            cursor: 'pointer'
          }}
          onClick={() => setShowCloseModal(true)}
        >
          Month-End Closing
        </button>
      </div>

      <div style={localStyles.kpiGrid}>
        <div style={localStyles.kpiCard}>
          <div style={localStyles.kpiIcon}>💰</div>
          <div>
            <div style={localStyles.kpiLabel}>Total Revenue</div>
            <div style={localStyles.kpiValue}>{orderTotals.totalRevenue.toLocaleString()} UGX</div>
          </div>
        </div>
        <div style={localStyles.kpiCard}>
          <div style={localStyles.kpiIcon}>🏭</div>
          <div>
            <div style={localStyles.kpiLabel}>Total COGS</div>
            <div style={localStyles.kpiValue}>{orderTotals.totalCOGS.toLocaleString()} UGX</div>
          </div>
        </div>
        <div style={localStyles.kpiCard}>
          <div style={localStyles.kpiIcon}>📈</div>
          <div>
            <div style={localStyles.kpiLabel}>Gross Profit</div>
            <div style={localStyles.kpiValue}>{orderTotals.grossProfit.toLocaleString()} UGX</div>
          </div>
        </div>
        <div style={localStyles.kpiCard}>
          <div style={localStyles.kpiIcon}>🎯</div>
          <div>
            <div style={localStyles.kpiLabel}>Profit Margin</div>
            <div style={localStyles.kpiValue}>{orderTotals.margin}%</div>
          </div>
        </div>
      </div>

      <div style={localStyles.filtersBar}>
        <div style={localStyles.filterGroup}>
          <label style={localStyles.filterLabel}>Date Range</label>
          <select style={localStyles.filterSelect} value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="year">Last 12 Months</option>
            <option value="all">All Time</option>
          </select>
        </div>
        <div style={localStyles.filterGroup}>
          <label style={localStyles.filterLabel}>Filter by Product</label>
          <select style={localStyles.filterSelect} value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value)}>
            <option value="all">All Products</option>
            {products.map(product => (
              <option key={product.id} value={product.id}>{product.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={localStyles.tableWrapper}>
        <table style={localStyles.table}>
          <thead>
            <tr style={localStyles.tableHeaderRow}>
              <th style={localStyles.tableHeader}>Date</th>
              <th style={localStyles.tableHeader}>Order #</th>
              <th style={localStyles.tableHeader}>Client</th>
              <th style={localStyles.tableHeader}>Phone</th>
              <th style={localStyles.tableHeader}>Products</th>
              <th style={localStyles.tableHeader}>Revenue</th>
              <th style={localStyles.tableHeader}>COGS</th>
              <th style={localStyles.tableHeader}>Gross Profit</th>
              <th style={localStyles.tableHeader}>Margin</th>
              <th style={localStyles.tableHeader}>Status</th>
            </tr>
          </thead>
          <tbody>
            {sales.map(sale => {
              const filteredItems = selectedProductId === 'all' 
                ? sale.items 
                : sale.items.filter(item => item.productId === selectedProductId);
              
              if (filteredItems.length === 0) return null;
              
              const revenue = filteredItems.reduce((sum, item) => sum + (item.totalSellingPrice || 0), 0);
              const cogs = filteredItems.reduce((sum, item) => sum + (item.totalCOGS || 0), 0);
              const profit = revenue - cogs;
              const marginPercent = revenue > 0 ? (profit / revenue * 100).toFixed(1) : 0;
              
              return (
                <tr key={sale.id} style={localStyles.tableRow}>
                  <td style={localStyles.tableCell}>{new Date(sale.orderDate?.toDate()).toLocaleDateString()}</td>
                  <td style={{...localStyles.tableCell, ...localStyles.orderNumber}}>{sale.orderNumber}</td>
                  <td style={localStyles.tableCell}><strong>{sale.clientName}</strong></td>
                  <td style={localStyles.tableCell}>{sale.clientPhone || '-'}</td>
                  <td style={localStyles.tableCell}>
                    <div style={localStyles.productsList}>
                      {filteredItems.map(item => (
                        <span key={item.id} style={localStyles.productTag}>
                          {item.productName} x{item.quantity}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={localStyles.tableCell}>{revenue.toLocaleString()} UGX</td>
                  <td style={localStyles.tableCell}>{cogs.toLocaleString()} UGX</td>
                  <td style={{...localStyles.tableCell, ...(profit > 0 ? localStyles.profitPositive : localStyles.profitNegative)}}>
                    {profit.toLocaleString()} UGX
                  </td>
                  <td style={{...localStyles.tableCell, ...(marginPercent > 0 ? localStyles.profitPositive : localStyles.profitNegative)}}>
                    {marginPercent}%
                  </td>
                  <td style={localStyles.tableCell}>
                    <span style={{...localStyles.statusBadge, ...localStyles[`status${sale.status}`]}}>
                      {sale.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCloseModal && (
        <div style={modalStyles.overlay} onClick={() => setShowCloseModal(false)}>
          <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
            <div style={modalStyles.header}>
              <h2 style={modalStyles.title}>Month-End Closing</h2>
              <button style={modalStyles.closeButton} onClick={() => setShowCloseModal(false)}>×</button>
            </div>
            
            <div style={modalStyles.form}>
              {closedPeriods.length > 0 && (
                <div style={{ marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid #eef2f6' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>Previously Closed Periods</h4>
                  {closedPeriods.map(period => (
                    <div key={period.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <span>{period.periodName}</span>
                      <button 
                        style={{ padding: '4px 10px', background: '#f1f5f9', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}
                        onClick={() => generateMonthEndReport(period)}
                      >
                        View Report
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {availableMonths.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>Available Months to Close</h4>
                  {availableMonths.map(month => (
                    <div key={month.period} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#f8fafc', borderRadius: '12px', marginBottom: '8px' }}>
                      <div>
                        <strong>{month.name}</strong>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{month.orderCount} orders · {month.totalRevenue.toLocaleString()} UGX</div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button 
                          style={{ padding: '6px 12px', background: '#f1f5f9', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                          onClick={() => generateMonthEndReport(month)}
                        >
                          Report
                        </button>
                        <button 
                          style={{ padding: '6px 12px', background: '#7f1a2e', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                          onClick={() => closeMonth(month)}
                          disabled={closingInProgress}
                        >
                          {closingInProgress ? 'Closing...' : 'Close Month'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {availableMonths.length === 0 && closedPeriods.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No sales data available for month-end closing.</div>
              )}

              <div style={{ marginTop: '20px', padding: '12px', background: '#fef3c7', borderRadius: '10px', fontSize: '12px', color: '#d97706' }}>
                <strong>Important:</strong> Once a month is closed, you cannot modify any sales from that period. This action is irreversible.
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button style={{ flex: 1, background: '#f1f5f9', color: '#334155', padding: '12px', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }} onClick={() => setShowCloseModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles (unchanged from original, kept for brevity)
const localStyles = {
  container: { padding: '28px' },
  viewToggle: { display: 'flex', gap: '12px', marginBottom: '24px', background: '#f1f5f9', padding: '4px', borderRadius: '12px', width: 'fit-content' },
  toggleButton: { padding: '8px 20px', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', background: 'transparent', color: '#64748b', transition: 'all 0.2s ease' },
  toggleButtonActive: { background: '#ffffff', color: '#7f1a2e', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', color: '#64748b' },
  spinner: { width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTopColor: '#7f1a2e', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '16px' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '28px' },
  kpiCard: { background: '#ffffff', padding: '20px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '16px', border: '1px solid #eef2f6', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' },
  kpiIcon: { fontSize: '32px' },
  kpiLabel: { fontSize: '12px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: '4px' },
  kpiValue: { fontSize: '22px', fontWeight: '700', color: '#0f172a' },
  filtersBar: { display: 'flex', gap: '20px', marginBottom: '24px', flexWrap: 'wrap' },
  filterGroup: { display: 'flex', alignItems: 'center', gap: '12px' },
  filterLabel: { fontSize: '13px', fontWeight: '500', color: '#475569' },
  filterSelect: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', background: '#ffffff', cursor: 'pointer' },
  tableWrapper: { overflowX: 'auto', borderRadius: '12px', border: '1px solid #eef2f6' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  tableHeaderRow: { background: '#f8fafc', borderBottom: '1px solid #eef2f6' },
  tableHeader: { textAlign: 'left', padding: '14px 16px', fontWeight: '600', color: '#475569' },
  tableRow: { borderBottom: '1px solid #f1f5f9' },
  tableCell: { padding: '14px 16px', color: '#334155' },
  orderNumber: { fontWeight: '600', color: '#7f1a2e' },
  productsList: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  productTag: { background: '#f1f5f9', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', color: '#475569' },
  statusBadge: { display: 'inline-block', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '500' },
  statusdeposit: { background: '#fef3c7', color: '#d97706' },
  statusproduction: { background: '#dbeafe', color: '#2563eb' },
  statuscompleted: { background: '#d1fae5', color: '#059669' },
  statusdelivered: { background: '#e0e7ff', color: '#4f46e5' },
  profitPositive: { color: '#10b981', fontWeight: '600' },
  profitNegative: { color: '#ef4444', fontWeight: '600' },
  emptyState: { textAlign: 'center', padding: '48px', color: '#94a3b8', fontSize: '13px' }
};

const modalStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  container: { background: '#ffffff', borderRadius: '24px', width: '90%', maxWidth: '550px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #eef2f6' },
  title: { fontSize: '20px', fontWeight: '600', color: '#0f172a' },
  closeButton: { background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#94a3b8' },
  form: { padding: '24px' },
  formGroup: { marginBottom: '20px' },
  formRow: { display: 'flex', gap: '16px', marginBottom: '20px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#334155', marginBottom: '8px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', outline: 'none' },
  textarea: { resize: 'vertical', fontFamily: 'inherit' },
  select: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', background: '#ffffff', cursor: 'pointer' },
  actions: { display: 'flex', gap: '12px', marginTop: '24px' },
  buttonPrimary: { flex: 1, background: '#7f1a2e', color: '#ffffff', padding: '12px', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  buttonSecondary: { flex: 1, background: '#f1f5f9', color: '#334155', padding: '12px', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  warningNote: { marginTop: '20px', padding: '12px', background: '#fef3c7', borderRadius: '10px', fontSize: '12px', color: '#d97706' }
};

const cogsModalStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 },
  container: { background: '#ffffff', borderRadius: '24px', width: '90%', maxWidth: '700px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #eef2f6' },
  title: { fontSize: '20px', fontWeight: '600', color: '#0f172a' },
  closeButton: { background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#94a3b8' },
  form: { padding: '24px' },
  formGroup: { marginBottom: '20px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#334155', marginBottom: '8px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', outline: 'none' },
  actions: { display: 'flex', gap: '12px', marginTop: '24px' },
  buttonPrimary: { flex: 1, background: '#7f1a2e', color: '#ffffff', padding: '12px', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  buttonSecondary: { flex: 1, background: '#f1f5f9', color: '#334155', padding: '12px', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  warningNote: { marginTop: '20px', padding: '12px', background: '#fef3c7', borderRadius: '10px', fontSize: '12px', color: '#d97706' }
};

const detailModalStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 },
  container: { background: '#ffffff', borderRadius: '24px', width: '90%', maxWidth: '1200px', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 24px', borderBottom: '1px solid #eef2f6', position: 'sticky', top: 0, background: '#ffffff' },
  title: { fontSize: '18px', fontWeight: '600', color: '#0f172a', margin: 0 },
  subtitle: { fontSize: '12px', color: '#64748b', marginTop: '4px' },
  closeButton: { background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#94a3b8' },
  summarySection: { padding: '20px 24px', borderBottom: '1px solid #eef2f6', background: '#f8fafc' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' },
  summaryItem: { textAlign: 'center', padding: '12px', background: '#ffffff', borderRadius: '10px', border: '1px solid #eef2f6' },
  summaryLabel: { fontSize: '11px', fontWeight: '500', textTransform: 'uppercase', color: '#64748b', marginBottom: '4px' },
  summaryValue: { fontSize: '16px', fontWeight: '700', color: '#0f172a' },
  tableSection: { padding: '20px 24px' },
  sectionTitle: { fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' },
  tableWrapper: { overflowX: 'auto', borderRadius: '12px', border: '1px solid #eef2f6' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  tableHeaderRow: { background: '#f8fafc', borderBottom: '1px solid #eef2f6' },
  tableHeader: { textAlign: 'left', padding: '10px 12px', fontWeight: '600', color: '#475569' },
  tableRow: { borderBottom: '1px solid #f1f5f9' },
  tableCell: { padding: '10px 12px', color: '#334155' },
  addCostButton: { padding: '4px 8px', background: '#f59e0b', color: '#ffffff', border: 'none', borderRadius: '4px', fontSize: '10px', cursor: 'pointer' },
  tableFooterRow: { background: '#f1f5f9', fontWeight: 'bold' },
  tableFooterLabel: { textAlign: 'right', fontWeight: 'bold', padding: '10px 12px' },
  tableFooterValue: { fontWeight: 'bold', padding: '10px 12px', color: '#7f1a2e' },
  statusBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: '500' },
  statusdeposit: { background: '#fef3c7', color: '#d97706' },
  statusproduction: { background: '#dbeafe', color: '#2563eb' },
  statuscompleted: { background: '#d1fae5', color: '#059669' },
  statusdelivered: { background: '#e0e7ff', color: '#4f46e5' },
  footer: { padding: '16px 24px', borderTop: '1px solid #eef2f6', display: 'flex', justifyContent: 'flex-end' },
  closeButtonFooter: { padding: '8px 20px', background: '#f1f5f9', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', color: '#475569' }
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
document.head.appendChild(styleSheet);

export default SalesLedger;